import { money, percent } from './format';

/**
 * Percentage helpers for the withdrawal modals.
 *
 * Shared by the investor and dealer tabs so the two can never word or round the same figure
 * differently — the tranche list and the live line below the amount field both come from here.
 */

const pct = (part: number, whole: number) => (whole > 0 ? (part / whole) * 100 : 0);

/**
 * Label for one tranche in the "select a tranche" dropdown: its remaining capital paired with the
 * monthly profit rate that capital earns. The rate is what makes the choice between two equal-sized
 * tranches meaningful — it is the earning power being given up by withdrawing from this one.
 */
export function trancheOptionLabel(
  id: number,
  dateLabel: string,
  remaining: number,
  profitRate: number,
): string {
  return `#${id} — ${dateLabel} — remaining ${money(remaining)} — ${percent(profitRate)}`;
}

export interface WithdrawalShare {
  /** True when the amount exceeds what is left in the chosen tranche. */
  overdrawn: boolean;
  text: string;
}

/**
 * The live line shown once an amount is entered: what fraction of the chosen tranche it takes, and
 * what fraction of everything still invested. A bare figure never answers "is this a small drawdown
 * or nearly all of it?" — the percentages do.
 */
export function withdrawalShare(
  amount: number | undefined,
  trancheId: number,
  trancheRemaining: number,
  totalRemaining: number,
): WithdrawalShare | null {
  if (!amount || amount <= 0) return null;

  if (amount > trancheRemaining) {
    return {
      overdrawn: true,
      text: `${money(amount)} is more than the ${money(trancheRemaining)} left in tranche #${trancheId}.`,
    };
  }

  return {
    overdrawn: false,
    text:
      `${pct(amount, trancheRemaining).toFixed(2)}% of tranche #${trancheId} ` +
      `(${money(trancheRemaining)} remaining) · ` +
      `${pct(amount, totalRemaining).toFixed(2)}% of the ${money(totalRemaining)} still invested.`,
  };
}
